import { ALPHABET } from "@/lib/tool-kit/ciphers/alphabet";
import type {
  Cryptogram,
  Token,
} from "@/lib/tool-kit/games-organizers/cryptogram";

function Cell({ token, answers }: { token: Token; answers: boolean }) {
  if (token.kind === "mark") {
    return (
      <span className="flex w-3 flex-col items-center">
        <span className="font-mono text-base leading-6">{token.char}</span>
        <span className="h-7" />
      </span>
    );
  }
  const filled = answers || token.given;
  return (
    <span className="flex w-7 flex-col items-center">
      <span className="font-mono text-base leading-6">{token.cipher}</span>
      <span
        className={`h-7 w-full border-current border-b text-center font-mono text-base leading-7 ${answers && !token.given ? "text-muted-foreground print:text-black" : "font-bold"}`}
      >
        {filled ? token.plain : ""}
      </span>
    </span>
  );
}

/** The cipher letters, each over a line for the plain letter; given letters are filled in. */
export function CryptogramGrid({
  puzzle,
  answers,
  label,
}: {
  puzzle: Cryptogram;
  answers: boolean;
  label: string;
}) {
  return (
    <div
      aria-label={label}
      className="flex flex-wrap gap-x-5 gap-y-4"
      role="img"
    >
      {puzzle.words.map((word, w) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: words are positional and may repeat
        <span className="inline-flex" key={w}>
          {word.map((token, c) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
            <Cell answers={answers} key={c} token={token} />
          ))}
        </span>
      ))}
    </div>
  );
}

/** How often each cipher letter occurs: the solver's first clue. */
export function FrequencyTable({
  counts,
  label,
}: {
  counts: readonly number[];
  label: string;
}) {
  return (
    <div aria-label={label} className="flex flex-wrap gap-1" role="img">
      {[...ALPHABET].map((letter, i) => (
        <span
          className="flex w-8 flex-col items-center border border-current py-0.5 font-mono text-sm"
          key={letter}
        >
          <span>{letter}</span>
          <span>{counts[i] || ""}</span>
        </span>
      ))}
    </div>
  );
}

/** The key: plain A–Z over the cipher letters that stand for them. */
export function KeyTable({
  puzzle,
  plainLabel,
  cipherLabel,
}: {
  puzzle: Cryptogram;
  plainLabel: string;
  cipherLabel: string;
}) {
  const rows = [
    { label: plainLabel, letters: [...ALPHABET] },
    { label: cipherLabel, letters: [...puzzle.key] },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse font-mono text-sm">
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th className="pe-3 text-left font-medium font-sans" scope="row">
                {row.label}
              </th>
              {row.letters.map((letter, i) => (
                <td
                  className="w-5 border border-current text-center"
                  key={ALPHABET.charAt(i)}
                >
                  {letter}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
